import {
  expectedOf,
  foundCounts,
  identicalCount,
  noFoundCount,
  waitingCount,
} from '@/lib/npc-expected-synthesis';
import { approximateEuros, replaysWord } from '@/lib/npc-launch';
import { eurosLabel, modelName } from '@/lib/npc-replays-labels';
import { REPLAY_MODE_LABEL, replayStartedLabel } from '@/lib/replay-mark';
import { sessionTime } from '@/lib/session-labels';
import type {
  NpcExpectedNoyau,
  NpcReplayRow,
  NpcRunOutcome,
  NpcSeriesDetail,
  NpcSeriesRestart,
  NpcSeriesRun,
} from '@matter_extractor/shared';
import { NPC_RUN_OUTCOME_LABELS } from '@matter_extractor/shared';

/**
 * What the page of a series says (npc.md §9, issue #210), PURE: its head, its totals, the state of
 * each slot and the order the table lists them in. A slot lists what the session RECORDED, and
 * since #216 (npc.md §26.5, lot L) the verdict of its found noyau against the expected one that the
 * server computed; these functions word figures the contract already carries and compute none
 * beyond a count of the slots on screen - no linter, no second series beside the first (N10).
 */

/** « Petit banc #204 · 27 oct. 2026, 14:02 »: the title of the page. */
export function seriesTitle(
  series: Pick<NpcSeriesDetail, 'target' | 'createdAt' | 'label'>,
): string {
  const name = series.target.kind === 'bench' ? series.target.benchName : series.target.npcName;
  const when = replayStartedLabel(series.createdAt).replace(' à ', ', ');
  return `${series.label ?? name} · ${when}`;
}

/** The line under the title, without the links: the experience, the side, the mode, the player. */
export function seriesHeadFacts(series: NpcSeriesDetail): string {
  const version =
    series.experienceVersionNumber === null ? '' : ` v${series.experienceVersionNumber}`;
  const side = series.tag === 'production' ? 'côté production' : 'côté développement';
  const player =
    series.player.kind === 'server'
      ? `joueur serveur · ${modelName(series.player.model.model)}`
      : 'joueur Claude, abonnement de l’opérateur';
  return [
    `${series.experienceName}${version} ${side}`,
    `mode ${REPLAY_MODE_LABEL[series.mode]}`,
    player,
  ].join(' · ');
}

/** « lancée par alice@exemple.fr à 14:02 », or « préparée par ... » for a series prepared for Claude. */
export function seriesLaunchedBy(
  series: Pick<NpcSeriesDetail, 'createdBy' | 'createdAt' | 'player' | 'label'>,
): string {
  // A seeded series gathers replays played before the NPC existed: nobody launched it.
  if (series.label !== null) return 'rangée après coup';
  const verb = series.player.kind === 'claude' ? 'préparée' : 'lancée';
  return `${verb} par ${series.createdBy} à ${sessionTime(series.createdAt)}`;
}

/** The effective slots: those a later slot does not supersede. */
export function effectiveRuns(runs: readonly NpcSeriesRun[]): NpcSeriesRun[] {
  return runs.filter((run) => run.relaunchedAt === null);
}

/** The ends that count as « finis », as the server counts them. */
export function outcomeCounts(
  runs: readonly NpcSeriesRun[],
): Partial<Record<NpcRunOutcome, number>> {
  const counts: Partial<Record<NpcRunOutcome, number>> = {};
  for (const run of effectiveRuns(runs)) {
    if (run.outcome === null) continue;
    counts[run.outcome] = (counts[run.outcome] ?? 0) + 1;
  }
  return counts;
}

/** « 13 / 16 , 2 départs, 1 fermée » after the figure: the ends that are not a plain finish. */
function remarkable(runs: readonly NpcSeriesRun[]): string {
  const counts = outcomeCounts(runs);
  const parts: string[] = [];
  const departed = counts.departed ?? 0;
  const closed = counts.closed ?? 0;
  const blocked = counts.blocked ?? 0;
  if (departed > 0) parts.push(`${departed} ${departed > 1 ? 'départs' : 'départ'}`);
  if (closed > 0) parts.push(`${closed} ${closed > 1 ? 'fermées' : 'fermée'}`);
  if (blocked > 0) parts.push(`${blocked} ${blocked > 1 ? 'bloquées' : 'bloquée'}`);
  return parts.length === 0 ? '' : `, ${parts.join(', ')}`;
}

/**
 * The totals line: « Rejeux finis 3 / 16 · coût jusqu'ici 5,02 € dispositif + 0,97 € joueur · estimé
 * au lancement : environ 21 € + 4 € ». A series prepared for Claude has no player cost (the
 * subscription), and a seeded series has no estimate.
 */
export function seriesTotals(series: NpcSeriesDetail): {
  readonly finished: string;
  readonly cost: string;
  readonly estimate: string | null;
} {
  const finished = `${series.finishedCount} / ${series.slotCount}${remarkable(series.runs)}`;
  const running = series.state === 'running' || series.state === 'waiting-claude';
  const cost = [
    `${running ? 'coût jusqu’ici' : 'coût'} ${eurosLabel(series.costEur)} dispositif`,
    series.player.kind === 'claude' ? null : `${eurosLabel(series.playerCostEur)} joueur`,
  ]
    .filter((part): part is string => part !== null)
    .join(' + ');
  const estimate = series.estimate;
  let estimated: string | null = null;
  if (estimate !== null && !estimate.impossible && estimate.dispositifEur !== null) {
    estimated =
      series.player.kind === 'claude' || estimate.playerEur === null
        ? `estimé au lancement : environ ${approximateEuros(estimate.dispositifEur)}`
        : `estimé au lancement : environ ${approximateEuros(estimate.dispositifEur)} + ${approximateEuros(estimate.playerEur)}`;
  } else if (estimate !== null) {
    estimated = 'estimation impossible au lancement';
  }
  return { finished, cost, estimate: estimated };
}

/** « Le serveur a redémarré à 14:32 : 4 rejeux interrompus » (§7.3). */
export function restartBannerTitle(restart: NpcSeriesRestart): string {
  return `Le serveur a redémarré à ${sessionTime(restart.at)} : ${replaysWord(restart.interruptedCount)} ${restart.interruptedCount > 1 ? 'interrompus' : 'interrompu'}`;
}

/** The slots a relaunch would replay: interrupted, and not already relaunched. */
export function relaunchableRuns(series: NpcSeriesDetail): readonly NpcSeriesRun[] {
  if (series.player.kind !== 'server' || series.state === 'stopped') return [];
  return series.runs.filter((run) => run.outcome === 'interrupted' && run.relaunchedAt === null);
}

/** « Relancer les rejeux interrompus (4) ». */
export function relaunchLabel(count: number): string {
  return `Relancer ${count > 1 ? 'les rejeux interrompus' : 'le rejeu interrompu'} (${count})`;
}

/** « Arrêter la série » is offered while something is left to stop. */
export function canStopSeries(series: Pick<NpcSeriesDetail, 'state'>): boolean {
  return series.state === 'running' || series.state === 'waiting-claude';
}

/** The slots a stop would interrupt or close, for the box: running ones and waiting ones. */
export function stopEffect(series: NpcSeriesDetail): { running: number; waiting: number } {
  const live = effectiveRuns(series.runs);
  return {
    running: live.filter((run) => run.state === 'running').length,
    waiting: live.filter((run) => run.state === 'waiting').length,
  };
}

/* ------------------------------------------------------------------- the rows */

/** What the issue cell of a slot says: the word, its tone, and what stands under it. */
export interface RunIssue {
  readonly label: string;
  readonly tone: 'info' | 'success' | 'neutral' | 'warning' | 'danger';
  readonly detail: string | null;
}

function queueDetail(rank: number): string {
  return rank === 1 ? '1er de la file du dev' : `${rank}e de la file du dev`;
}

const OUTCOME_TONE: Readonly<Record<NpcRunOutcome, RunIssue['tone']>> = {
  finished: 'success',
  closed: 'warning',
  departed: 'neutral',
  blocked: 'danger',
  interrupted: 'warning',
};

/** The issue of a slot: waiting (with its rank), running (with its turn and step), or how it ended. */
export function runIssue(run: NpcSeriesRun, series: Pick<NpcSeriesDetail, 'player'>): RunIssue {
  if (run.state === 'waiting') {
    if (series.player.kind === 'claude') {
      return { label: 'en attente de Claude', tone: 'info', detail: null };
    }
    return {
      label: 'en attente',
      tone: 'neutral',
      detail: run.rank === null ? null : queueDetail(run.rank),
    };
  }
  if (run.state === 'running') {
    const progress = run.progress;
    const where =
      progress === null
        ? null
        : progress.stepName === null
          ? `tour ${progress.turn}`
          : `tour ${progress.turn}, ${progress.stepName}`;
    const lead = run.relaunchOf === null ? null : 'relance de la ligne interrompue';
    return {
      label: 'en cours',
      tone: 'info',
      detail: [lead, where].filter((part): part is string => part !== null).join(' · ') || null,
    };
  }
  const outcome = run.outcome ?? 'finished';
  const detail: string[] = [];
  if (run.reason !== null) detail.push(run.reason);
  if (run.relaunchedAt !== null) detail.push(`relancée à ${sessionTime(run.relaunchedAt)}`);
  return {
    label: NPC_RUN_OUTCOME_LABELS[outcome],
    tone: OUTCOME_TONE[outcome],
    detail: detail.length === 0 ? null : detail.join(' · '),
  };
}

/** One group of the table: a NPC (or the NPC the slot stood for) and its slots by pass. */
export interface RunGroup {
  readonly npcId: string;
  readonly npcName: string;
  readonly versionNumber: number;
  readonly runs: readonly NpcSeriesRun[];
}

/**
 * The slots grouped by NPC, in the order the NPC come, each group by pass - and a slot that
 * relaunches another right under the one it relaunches (§7.3: the interrupted one stays).
 */
export function runGroups(runs: readonly NpcSeriesRun[]): readonly RunGroup[] {
  const order: string[] = [];
  const byNpc = new Map<string, NpcSeriesRun[]>();
  for (const run of runs) {
    const list = byNpc.get(run.npcId);
    if (list === undefined) {
      order.push(run.npcId);
      byNpc.set(run.npcId, [run]);
    } else list.push(run);
  }
  return order.flatMap((npcId) => {
    const list = byNpc.get(npcId) ?? [];
    const first = list[0];
    if (first === undefined) return [];
    const sorted = [...list].sort(
      (a, b) => a.pass - b.pass || Number(a.relaunchOf !== null) - Number(b.relaunchOf !== null),
    );
    return [
      {
        npcId,
        npcName: first.npcName,
        versionNumber: first.npcVersionNumber,
        runs: sorted,
      },
    ];
  });
}

/** Does the slot have a session to open or to watch? A purge leaves the slot without one. */
export function runHasSession(run: NpcSeriesRun): run is NpcSeriesRun & { sessionId: string } {
  return run.sessionId !== null;
}

/* ------------------------------------------------- the line of a group (§26.5) */

/** What the header of a group of NPC says of the expected noyau of its slots in this series. */
export interface GroupExpectedSummary {
  /** At least one slot of the group has a replay: without one nothing says what the sheet expects. */
  readonly known: boolean;
  readonly expected: NpcExpectedNoyau | null;
  /** « trouvé PSG × 2, identique × 0 »: null when there is no replay to count or no triplet expected. */
  readonly text: string | null;
}

/** The replays of the effective slots of a group (a superseded interrupted slot does not count twice). */
export function groupReplays(group: Pick<RunGroup, 'runs'>): NpcReplayRow[] {
  return group.runs.flatMap((run) =>
    run.relaunchedAt === null && run.replay !== null ? [run.replay] : [],
  );
}

/**
 * The summary line of a group (npc.md §26.5, planche 4): « attendu SGP · trouvé PSG × 2, identique
 * × 0 ». A series plays ONE version of ONE experience, so a group never totals two experiences; the
 * counts are those of the replays of this series alone. Without an expected noyau on the sheet it
 * says no figure (the screen says « pas de noyau attendu sur sa fiche »), and « hors base » says
 * the found ones only: there is nothing to be identical to.
 */
export function groupExpectedSummary(group: Pick<RunGroup, 'runs'>): GroupExpectedSummary {
  const replays = groupReplays(group);
  if (replays.length === 0) return { known: false, expected: null, text: null };
  const expected = replays.map(expectedOf).find((one) => one !== null) ?? null;
  if (expected === null) return { known: true, expected: null, text: null };

  const found = foundCounts(replays);
  const parts: string[] = [];
  if (found.length > 0) {
    parts.push(`trouvé ${found.map((entry) => `${entry.noyau} × ${entry.count}`).join(', ')}`);
  }
  const none = noFoundCount(replays);
  if (none > 0) parts.push(`pas de trouvé × ${none}`);
  const identical = identicalCount(replays);
  if (identical !== null) parts.push(`identique × ${identical}`);
  const waiting = waitingCount(replays);
  if (waiting > 0) parts.push(`en cours × ${waiting}`);
  return { known: true, expected, text: parts.length === 0 ? null : parts.join(', ') };
}
